// Mirrors the URL detection in the mobile app's Announcement/TextContainer so a post
// with links reads the same on both platforms.
const URL_REGEX = /((https?:\/\/)|(www\.))[^\s/$.?#].[^\s]*/gi;

// A match glued to the token before it is part of that token, not a link of its own —
// the address in `coach@www.example.com`, the amount in `$5.www.scam.com`.
const ATTACHED_BEFORE_REGEX = /[\w@.\-/:]/;

// The match runs to the next whitespace, so sentence punctuation ends up inside it.
// Trimming keeps the href from 404ing on a full stop or a closing bracket.
const TRAILING_PUNCTUATION = '.,;:!?)]\'"`';

const CLOSING_TO_OPENING = { ')': '(', ']': '[' };

const countOccurrences = (text, char) => text.split(char).length - 1;

/**
 * Length of `match` with trailing sentence punctuation removed. A closing bracket is
 * kept when the match opens one, so https://en.wikipedia.org/wiki/Foo_(bar) survives.
 *
 * @param {string} match The matched URL, possibly with punctuation attached.
 * @returns {number} The length of the URL once trailing punctuation is dropped.
 */
const trimTrailingPunctuation = (match) => {
  let end = match.length;

  while (end > 0) {
    const char = match[end - 1];
    if (!TRAILING_PUNCTUATION.includes(char)) {
      break;
    }

    const opening = CLOSING_TO_OPENING[char];
    const candidate = match.slice(0, end);
    if (opening && countOccurrences(candidate, opening) >= countOccurrences(candidate, char)) {
      break;
    }

    end -= 1;
  }

  return end;
};

/**
 * Splits text into alternating plain-text and URL segments. Concatenating the segment
 * texts reproduces the input exactly, so nothing is dropped or duplicated.
 *
 * @param {string} text The text to scan for URLs.
 * @returns {Array<{ text: string, url: string|null, index: number }>} The segments in order,
 * where `url` is the href for a link segment and null for plain text.
 */
const splitTextByLinks = (text = '') => {
  const segments = [];
  let lastIndex = 0;

  text.replace(URL_REGEX, (match, protocolMatch, httpMatch, wwwMatch, offset) => {
    // Leaving lastIndex alone lets a skipped match fall into the next plain-text segment.
    if (offset > 0 && ATTACHED_BEFORE_REGEX.test(text[offset - 1])) {
      return match;
    }

    const urlLength = trimTrailingPunctuation(match);
    if (urlLength === 0) {
      return match;
    }

    const matchedUrl = match.slice(0, urlLength);

    if (offset > lastIndex) {
      segments.push({ text: text.slice(lastIndex, offset), url: null, index: lastIndex });
    }

    // Bare www. links need a scheme or the browser resolves them as a relative path.
    const url = /^https?:\/\//i.test(matchedUrl) ? matchedUrl : `https://${matchedUrl}`;
    segments.push({ text: matchedUrl, url, index: offset });

    lastIndex = offset + urlLength;
    return match;
  });

  if (lastIndex < text.length) {
    segments.push({ text: text.slice(lastIndex), url: null, index: lastIndex });
  }

  return segments;
};

export { splitTextByLinks };
